/// <amd-module name="Test/FilteringLogic/Sources/TrendChartSource" />
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import { TrendChartOptions } from "Test/Analytics/Common/Definitions";
import { TestReportSource } from "Test/Analytics/Common/Sources/TestReportSource";
import * as CommonTypes from "Test/Analytics/Common/Types";
import { DataSourceHelper } from "Test/Analytics/Common/Utils/DataSourceHelper";
import { ITrendChartAggregateData, ITrendChartData } from "Test/Analytics/TrendChartCommon/Types";
import { union } from "Test/common-util/Common.Utils";
import * as Resources from "Test/FilteringLogic/Resources";
import { ITfsPageService } from "Tfs/Platform/Page";
import * as Utils_String from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";

export class TrendChartSource extends TestReportSource {
    constructor(pageContext: IVssPageContext) {
        super("TestResultsAnalytics_InContext_TrendChartReport", pageContext);
    }

    public queryTrendChartData(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration,
        chartMetric: CommonTypes.Metric
    ): Promise<ITrendChartData> {
        switch (chartMetric) {
            case CommonTypes.Metric.ResultCountAndPassRate:
                switch (DataSourceHelper.getTrendChartDataSourceTable(confValues)) {
                    case CommonTypes.DataSourceTable.TestResult:
                        return this._queryTestOutcomeCountAndPassRateTrendData_TestResultTable(testReportContext, confValues);
                    case CommonTypes.DataSourceTable.TestResultDaily:
                        return this._queryTestOutcomeCountAndPassRateTrendData_TestResultDailyTable(testReportContext, confValues);
                    case CommonTypes.DataSourceTable.TestRun:
                        return this._queryTestOutcomeCountAndPassRateTrendData_TestRunTable(testReportContext, confValues);
                    default:
                        return this._queryTestOutcomeCountAndPassRateTrendData_TestResultTable(testReportContext, confValues);
                }
                break;
            case CommonTypes.Metric.ResultCount:
                // TODO (akgup): Combine APIs for all groupBy into one.
                switch (confValues.groupBy) {
                    case CommonTypes.GroupBy.None:
                        switch (DataSourceHelper.getTrendChartDataSourceTable(confValues)) {
                            case CommonTypes.DataSourceTable.TestResult:
                                return this._queryTestOutcomeCountTrendData_TestResultTable(testReportContext, confValues);
                            case CommonTypes.DataSourceTable.TestResultDaily:
                                return this._queryTestOutcomeCountTrendData_TestResultDailyTable(testReportContext, confValues);
                            case CommonTypes.DataSourceTable.TestRun:
                                return this._queryTestOutcomeCountTrendData_TestRunTable(testReportContext, confValues);
                            default:
                                return this._queryTestOutcomeCountAndPassRateTrendData_TestResultTable(testReportContext, confValues);
                        }
                    default:
                        switch (DataSourceHelper.getTrendChartDataSourceTable(confValues)) {
                            case CommonTypes.DataSourceTable.TestResult:
                                return this._queryTestOutcomeCountStackedTrendData_TestResultTable(testReportContext, confValues);
                            case CommonTypes.DataSourceTable.TestResultDaily:
                                return this._queryTestOutcomeCountStackedTrendData_TestResultDailyTable(testReportContext, confValues);
                            case CommonTypes.DataSourceTable.TestRun:
                                return this._queryTestOutcomeCountStackedTrendData_TestRunTable(testReportContext, confValues);
                            default:
                                return this._queryTestOutcomeCountAndPassRateTrendData_TestResultTable(testReportContext, confValues);
                        }
                }
                break;
            case CommonTypes.Metric.AvgDuration:
                // TODO (akgup): Combine APIs for all groupBy into one.
                switch (confValues.groupBy) {
                    case CommonTypes.GroupBy.None:
                        switch (DataSourceHelper.getTrendChartDataSourceTable(confValues)) {
                            case CommonTypes.DataSourceTable.TestResult:
                                return this._queryTestDurationTrendData_TestResultTable(testReportContext, confValues);
                            case CommonTypes.DataSourceTable.TestResultDaily:
                                return this._queryTestDurationTrendData_TestResultDailyTable(testReportContext, confValues);
                            case CommonTypes.DataSourceTable.TestRun:
                                return this._queryTestDurationTrendData_TestRunTable(testReportContext, confValues);
                            default:
                                return this._queryTestOutcomeCountAndPassRateTrendData_TestResultTable(testReportContext, confValues);
                        }
                    default:
                        switch (DataSourceHelper.getTrendChartDataSourceTable(confValues)) {
                            case CommonTypes.DataSourceTable.TestResult:
                                return this._queryTestDurationStackedTrendData_TestResultTable(testReportContext, confValues);
                            case CommonTypes.DataSourceTable.TestResultDaily:
                                return this._queryTestDurationStackedTrendData_TestResultDailyTable(testReportContext, confValues);
                            case CommonTypes.DataSourceTable.TestRun:
                                return this._queryTestDurationStackedTrendData_TestRunTable(testReportContext, confValues);
                            default:
                                return this._queryTestOutcomeCountAndPassRateTrendData_TestResultTable(testReportContext, confValues);
                        }
                }
                break;
            default:
                return this._queryTestOutcomeCountAndPassRateTrendData_TestResultTable(testReportContext, confValues);
        }
    }

    protected _getOrderByDateClause(trendBy: CommonTypes.TrendBy): string {
        switch (trendBy) {
            case CommonTypes.TrendBy.Days:
                return " CompletedDateSK asc ";
            case CommonTypes.TrendBy.Weeks:
                return " CompletedOn/WeekStartingDate asc ";
        }
    }

    protected _getDailyOrderByDateClause(trendBy: CommonTypes.TrendBy): string {
        switch (trendBy) {
            case CommonTypes.TrendBy.Days:
                return "DateSK asc";
            case CommonTypes.TrendBy.Weeks:
                return "Date/WeekStartingDate asc";
        }
    }

    private _queryTestOutcomeCountAndPassRateTrendData_TestRunTable(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<ITrendChartData> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            const projectId = tfsPageData.project.id;
            const queryOptions = {
                entityType: this._testRunEntityName,
                project: projectId
            } as ODataQueryOptions;

            this._getTestRunTitlePropertyString = () => {
                return "Title";
            };

            const filterStr = `filter(${this._getTestResultsContextFilter(testReportContext)} and ${this._getDateFilter(
                confValues.trendBy,
                confValues.period
            )} ${this._getFilterByFieldsString(confValues.configuredFilters)})`;

            const outcomeAggrStr: string = this.getOutcomeCountAggregationString(confValues.outcomes);

            const groupByStr = `groupby((${this._getGroupByPeriodColumnString(confValues.trendBy)}), aggregate(${outcomeAggrStr}))`;

            queryOptions.$apply = `${filterStr}/${groupByStr}`;
            queryOptions.$orderby = this._getOrderByDateClause(confValues.trendBy);

            return this._queryTestOutcomeCountAndPassRateTrendData(queryOptions, confValues);
        } else {
            throw new Error("Unable to fetch project details.");
        }
    }

    private _queryTestOutcomeCountAndPassRateTrendData_TestResultDailyTable(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<ITrendChartData> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            const projectId = tfsPageData.project.id;
            const queryOptions = {
                entityType: this._testResultDailyEntityName,
                project: projectId
            } as ODataQueryOptions;

            const requiredOutcomeFilters = [CommonTypes.TestOutcome.Passed, CommonTypes.TestOutcome.NotExecuted, CommonTypes.TestOutcome.NotImpacted];
            const outcomefilters = confValues.outcomes || ([] as CommonTypes.TestOutcome[]);
            const outcomeCountStrs = this._getOutcomeCountAggregateAndComputeStrings(union(outcomefilters, requiredOutcomeFilters));

            const filterStr = `filter(${this._getTestResultsContextPipelineFilter(testReportContext)} and ${this._getDailyDateFilter(
                confValues.trendBy,
                confValues.period
            )} ${this._getFilterByFieldsString(confValues.configuredFilters)})`;

            const groupByStr = `groupby((${this._getGroupByDailyPeriodColumnString(
                confValues.trendBy
            )}), aggregate(ResultCount with sum as ResultCount, ${outcomeCountStrs.outcomeAggregationStr}))`;

            queryOptions.$apply = `${filterStr}/${groupByStr}`;
            queryOptions.$orderby = this._getDailyOrderByDateClause(confValues.trendBy);

            return this._queryTestOutcomeCountAndPassRateTrendData(queryOptions, confValues, this._parseReturnDailyDateIntoDisplayForm);
        } else {
            throw new Error("Unable to fetch project details.");
        }
    }

    private _queryTestOutcomeCountAndPassRateTrendData_TestResultTable(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<ITrendChartData> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            const projectId = tfsPageData.project.id;
            const queryOptions = {
                entityType: this._testResultEntityName,
                project: projectId
            } as ODataQueryOptions;

            this._getTestRunTitlePropertyString = () => {
                return "TestRun/Title";
            };

            const filterStr = `filter(${this._getTestResultsContextFilter(testReportContext)} and ${this._getDateFilter(
                confValues.trendBy,
                confValues.period
            )} ${this._getFilterByFieldsString(confValues.configuredFilters)})`;

            let outcomeAggrStr = `$count as TotalCount, ${this._getOutcomeCountCastedAggregationString(
                CommonTypes.TestOutcome.Passed
            )}, ${this._getOutcomeCountCastedAggregationString(CommonTypes.TestOutcome.NotExecuted)},
${this._getOutcomeCountCastedAggregationString(CommonTypes.TestOutcome.NotImpacted)}`;

            confValues.outcomes.forEach(o => {
                if (o !== CommonTypes.TestOutcome.Passed && o !== CommonTypes.TestOutcome.NotExecuted && o !== CommonTypes.TestOutcome.NotImpacted) {
                    outcomeAggrStr += `, ${this._getOutcomeCountCastedAggregationString(o)}`;
                }
            });

            const groupByStr = `groupby((${this._getGroupByPeriodColumnString(confValues.trendBy)}), aggregate(${outcomeAggrStr}))`;
            queryOptions.$apply = `${filterStr}/${groupByStr}`;
            queryOptions.$orderby = this._getOrderByDateClause(confValues.trendBy);

            return this._queryTestOutcomeCountAndPassRateTrendData(queryOptions, confValues);
        } else {
            throw new Error("Unable to fetch project details.");
        }
    }

    private _queryTestOutcomeCountTrendData_TestRunTable(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<ITrendChartData> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            const projectId = tfsPageData.project.id;
            const queryOptions = {
                entityType: this._testRunEntityName,
                project: projectId
            } as ODataQueryOptions;

            this._getTestRunTitlePropertyString = () => {
                return "Title";
            };

            const filterStr = `filter(${this._getTestResultsContextFilter(testReportContext)} and ${this._getDateFilter(
                confValues.trendBy,
                confValues.period
            )} ${this._getFilterByFieldsString(confValues.configuredFilters)})`;

            let outcomeAggrStr: string = this._emptyString;
            if (confValues.outcomes.length === 0) {
                outcomeAggrStr = this._getTotalCountStringForAggregation();
            } else {
                confValues.outcomes.forEach(o => {
                    if (!outcomeAggrStr) {
                        outcomeAggrStr = this._getOutcomeCountStringForAggregation(o);
                    } else {
                        outcomeAggrStr += `, ${this._getOutcomeCountStringForAggregation(o)}`;
                    }
                });
            }

            const groupByStr = `groupby((${this._getGroupByPeriodColumnString(confValues.trendBy)}), aggregate(${outcomeAggrStr}))`;

            queryOptions.$apply = `${filterStr}/${groupByStr}`;
            queryOptions.$orderby = this._getOrderByDateClause(confValues.trendBy);

            return this._queryTestOutcomeCountTrendData(queryOptions, confValues);
        } else {
            throw new Error("Unable to fetch project details.");
        }
    }

    private _queryTestOutcomeCountTrendData_TestResultDailyTable(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<ITrendChartData> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            const projectId = tfsPageData.project.id;
            const queryOptions = {
                entityType: this._testResultDailyEntityName,
                project: projectId
            } as ODataQueryOptions;

            let outcomeAggrStr: string = this._emptyString;
            if (confValues.outcomes.length === 0) {
                outcomeAggrStr = this._getTotalCountStringForAggregation();
            } else {
                outcomeAggrStr = this._getOutcomeCountAggregateAndComputeStrings(confValues.outcomes).outcomeAggregationStr;
            }

            const filterStr = `filter(${this._getTestResultsContextPipelineFilter(testReportContext)} and ${this._getDailyDateFilter(
                confValues.trendBy,
                confValues.period
            )} ${this._getFilterByFieldsString(confValues.configuredFilters)})`;
            const groupByStr = `groupby((${this._getGroupByDailyPeriodColumnString(confValues.trendBy)}), aggregate(${outcomeAggrStr}))`;

            queryOptions.$apply = `${filterStr}/${groupByStr}`;
            queryOptions.$orderby = this._getDailyOrderByDateClause(confValues.trendBy);

            return this._queryTestOutcomeCountTrendData(queryOptions, confValues, this._parseReturnDailyDateIntoDisplayForm);
        } else {
            throw new Error("Unable to fetch project details.");
        }
    }

    private _queryTestOutcomeCountTrendData_TestResultTable(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<ITrendChartData> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            const projectId = tfsPageData.project.id;
            const queryOptions = {
                entityType: this._testResultEntityName,
                project: projectId
            } as ODataQueryOptions;

            this._getTestRunTitlePropertyString = () => {
                return "TestRun/Title";
            };

            const filterStr = `filter(${this._getTestResultsContextFilter(testReportContext)} and ${this._getDateFilter(
                confValues.trendBy,
                confValues.period
            )} ${this._getFilterByFieldsString(confValues.configuredFilters)})`;

            let outcomeAggrStr: string = this._emptyString;
            if (confValues.outcomes.length === 0) {
                outcomeAggrStr = "$count as TotalCount";
            } else {
                confValues.outcomes.forEach(o => {
                    if (!outcomeAggrStr) {
                        outcomeAggrStr = this._getOutcomeCountCastedAggregationString(o);
                    } else {
                        outcomeAggrStr += `, ${this._getOutcomeCountCastedAggregationString(o)}`;
                    }
                });
            }

            const groupByStr = `groupby((${this._getGroupByPeriodColumnString(confValues.trendBy)}), aggregate(${outcomeAggrStr}))`;

            queryOptions.$apply = `${filterStr}/${groupByStr}`;
            queryOptions.$orderby = this._getOrderByDateClause(confValues.trendBy);

            return this._queryTestOutcomeCountTrendData(queryOptions, confValues);
        } else {
            throw new Error("Unable to fetch project details.");
        }
    }

    private _queryTestOutcomeCountStackedTrendData_TestRunTable(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<ITrendChartData> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            const projectId = tfsPageData.project.id;
            const queryOptions = {
                entityType: this._testRunEntityName,
                project: projectId
            } as ODataQueryOptions;

            this._getTestRunTitlePropertyString = () => {
                return "Title";
            };

            const outcomeCountStrs = this._getOutcomeCountAggregateAndComputeStrings(confValues.outcomes);

            const filterStr = `filter(${this._getTestResultsContextFilter(testReportContext)} and ${this._getDateFilter(
                confValues.trendBy,
                confValues.period
            )} ${this._getFilterByFieldsString(confValues.configuredFilters)})`;
            const groupByStr = `groupby((${this._getGroupByTestRunPropertiesString(confValues.groupBy)}, ${this._getGroupByPeriodColumnString(
                confValues.trendBy
            )}),aggregate(${outcomeCountStrs.outcomeAggregationStr}))`;

            if (!outcomeCountStrs.outcomeComputeStr) {
                queryOptions.$apply = `${filterStr}/${groupByStr}`;
            } else {
                const computeStr = `compute((${outcomeCountStrs.outcomeComputeStr}) as AggregateValue)`;
                queryOptions.$apply = `${filterStr}/${groupByStr}/${computeStr}`;
            }

            // Filter out groups that have 0 AggregateValue as that is not relevant to be shown in chart
            queryOptions.$apply += "/filter(AggregateValue gt 0)";

            return this.queryOData(queryOptions).then((data: CommonTypes.IODataQueryResponse) => {
                return this._getStackedChartData(confValues, data, CommonTypes.Metric.ResultCount);
            });
        } else {
            throw new Error("Unable to fetch project details.");
        }
    }

    private _queryTestOutcomeCountStackedTrendData_TestResultDailyTable(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<ITrendChartData> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            const projectId = tfsPageData.project.id;
            const queryOptions = {
                entityType: this._testResultDailyEntityName,
                project: projectId
            } as ODataQueryOptions;

            const outcomeCountStrs = this._getOutcomeCountAggregateAndComputeStrings(confValues.outcomes);

            const filterStr = `filter(${this._getTestResultsContextPipelineFilter(testReportContext)} and ${this._getDailyDateFilter(
                confValues.trendBy,
                confValues.period
            )} ${this._getFilterByFieldsString(confValues.configuredFilters)})`;
            const groupByStr = `groupby((${this._getGroupByTestPropertiesString(confValues.groupBy)}, ${this._getGroupByDailyPeriodColumnString(
                confValues.trendBy
            )}), aggregate(${outcomeCountStrs.outcomeAggregationStr}))`;

            if (!outcomeCountStrs.outcomeComputeStr) {
                queryOptions.$apply = `${filterStr}/${groupByStr}`;
            } else {
                const computeStr = `compute((${outcomeCountStrs.outcomeComputeStr}) as AggregateValue)`;
                queryOptions.$apply = `${filterStr}/${groupByStr}/${computeStr}`;
            }

            // Filter out groups that have 0 AggregateValue as that is not relevant to be shown in chart
            queryOptions.$apply += "/filter(AggregateValue gt 0)";

            return this.queryOData(queryOptions).then((data: CommonTypes.IODataQueryResponse) => {
                return this._getStackedChartData(confValues, data, CommonTypes.Metric.ResultCount, this._parseReturnDailyDateIntoDisplayForm);
            });
        } else {
            throw new Error("Unable to fetch project details.");
        }
    }

    private _queryTestOutcomeCountStackedTrendData_TestResultTable(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<ITrendChartData> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            const projectId = tfsPageData.project.id;
            const queryOptions = {
                entityType: this._testResultEntityName,
                project: projectId
            } as ODataQueryOptions;

            this._getTestRunTitlePropertyString = () => {
                return "TestRun/Title";
            };

            let outcomeFilterStr: string = this._emptyString;
            if (confValues.outcomes.length > 0) {
                confValues.outcomes.forEach(o => {
                    if (!outcomeFilterStr) {
                        outcomeFilterStr = this._getOutcomeFilter(o);
                    } else {
                        outcomeFilterStr += ` or ${this._getOutcomeFilter(o)}`;
                    }
                });

                outcomeFilterStr = ` and ( ${outcomeFilterStr} ) `;
            }

            const filterStr = `filter(${this._getTestResultsContextFilter(testReportContext)} and ${this._getDateFilter(
                confValues.trendBy,
                confValues.period
            )} ${outcomeFilterStr} ${this._getFilterByFieldsString(confValues.configuredFilters)})`;
            const groupByStr = `groupby((${this._getGroupByTestPropertiesString(confValues.groupBy)}, ${this._getGroupByPeriodColumnString(
                confValues.trendBy
            )}), aggregate($count as AggregateValue))`;

            // Filter out groups that have 0 AggregateValue as that is not relevant to be shown in chart
            queryOptions.$apply = `${filterStr}/${groupByStr}/filter(AggregateValue gt 0)`;

            return this.queryOData(queryOptions).then((data: CommonTypes.IODataQueryResponse) => {
                return this._getStackedChartData(confValues, data, CommonTypes.Metric.ResultCount);
            });
        } else {
            throw new Error("Unable to fetch project details.");
        }
    }

    private _queryTestDurationTrendData_TestRunTable(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<ITrendChartData> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            const projectId = tfsPageData.project.id;
            const queryOptions = {
                entityType: this._testRunEntityName,
                project: projectId
            } as ODataQueryOptions;

            this._getTestRunTitlePropertyString = () => {
                return "Title";
            };

            const filterStr = `filter(${this._getTestResultsContextFilter(testReportContext)} and ${this._getDateFilter(
                confValues.trendBy,
                confValues.period
            )} ${this._getFilterByFieldsString(confValues.configuredFilters)})`;
            const groupByContextAggregationStr = `groupby((${this._getGroupByPeriodColumnString(
                confValues.trendBy
            )}, ${this._getGroupByTestResultsContext(testReportContext)})
, aggregate(ResultDurationSeconds with sum as ResultDurationSeconds))`;
            const groupByAggregationStr = `groupby((${this._getGroupByPeriodColumnString(
                confValues.trendBy
            )}), aggregate(ResultDurationSeconds with average as AvgDuration))`;

            queryOptions.$apply = `${filterStr}/${groupByContextAggregationStr}/${groupByAggregationStr}`;
            queryOptions.$orderby = this._getOrderByDateClause(confValues.trendBy);

            return this._queryTestDurationTrendData(queryOptions, confValues);
        } else {
            throw new Error("Unable to fetch project details.");
        }
    }

    private _queryTestDurationTrendData_TestResultDailyTable(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<ITrendChartData> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            const projectId = tfsPageData.project.id;
            const queryOptions = {
                entityType: this._testResultDailyEntityName,
                project: projectId
            } as ODataQueryOptions;

            const filterStr = `filter(${this._getTestResultsContextPipelineFilter(testReportContext)} and ${this._getDailyDateFilter(
                confValues.trendBy,
                confValues.period
            )} ${this._getFilterByFieldsString(confValues.configuredFilters)})`;
            const groupByContextAggregationStr = `groupby((TestSK, ${this._getGroupByDailyPeriodColumnString(
                confValues.trendBy
            )}), aggregate(ResultDurationSeconds div ResultCount with sum as TestAvgDuration))`;
            const groupByAggregationStr = `groupby((${this._getGroupByDailyPeriodColumnString(
                confValues.trendBy
            )}), aggregate(TestAvgDuration with sum as AvgDuration))`;

            queryOptions.$apply = `${filterStr}/${groupByContextAggregationStr}/${groupByAggregationStr}`;

            queryOptions.$orderby = this._getDailyOrderByDateClause(confValues.trendBy);

            return this._queryTestDurationTrendData(queryOptions, confValues, this._parseReturnDailyDateIntoDisplayForm);
        } else {
            throw new Error("Unable to fetch project details.");
        }
    }

    private _queryTestDurationTrendData_TestResultTable(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<ITrendChartData> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            const projectId = tfsPageData.project.id;
            const queryOptions = {
                entityType: this._testResultEntityName,
                project: projectId
            } as ODataQueryOptions;

            this._getTestRunTitlePropertyString = () => {
                return "TestRun/Title";
            };

            const filterStr = `filter(${this._getTestResultsContextFilter(testReportContext)} and ${this._getDateFilter(
                confValues.trendBy,
                confValues.period
            )} ${this._getFilterByFieldsString(confValues.configuredFilters)})`;
            const groupByContextAggregationStr = `groupby((${this._getGroupByPeriodColumnString(
                confValues.trendBy
            )}, ${this._getGroupByTestResultsContext(testReportContext)})
    , aggregate(DurationSeconds with sum as DurationSeconds))`;
            const groupByAggregationStr = `groupby((${this._getGroupByPeriodColumnString(
                confValues.trendBy
            )}), aggregate(DurationSeconds with average as AvgDuration))`;

            queryOptions.$apply = `${filterStr}/${groupByContextAggregationStr}/${groupByAggregationStr}`;
            queryOptions.$orderby = this._getOrderByDateClause(confValues.trendBy);

            return this._queryTestDurationTrendData(queryOptions, confValues);
        } else {
            throw new Error("Unable to fetch project details.");
        }
    }

    private _queryTestDurationStackedTrendData_TestRunTable(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<ITrendChartData> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            const projectId = tfsPageData.project.id;
            const queryOptions = {
                entityType: this._testRunEntityName,
                project: projectId
            } as ODataQueryOptions;

            this._getTestRunTitlePropertyString = () => {
                return "Title";
            };

            const filterStr = `filter(${this._getTestResultsContextFilter(testReportContext)} and ${this._getDateFilter(
                confValues.trendBy,
                confValues.period
            )} ${this._getFilterByFieldsString(confValues.configuredFilters)})`;
            const groupByContextAggregationStr = `groupby((${this._getGroupByTestRunPropertiesString(
                confValues.groupBy
            )}, ${this._getGroupByPeriodColumnString(confValues.trendBy)}, ${this._getGroupByTestResultsContext(testReportContext)})
    , aggregate(ResultDurationSeconds with sum as ResultDurationSeconds))`;
            const groupByAggregationStr = `groupby((${this._getGroupByTestRunPropertiesString(
                confValues.groupBy
            )}, ${this._getGroupByPeriodColumnString(confValues.trendBy)}), aggregate(ResultDurationSeconds with average as AggregateValue))`;

            // Filter out groups that have 0d(0.0) AggregateValue as that is not relevant to be shown in chart
            queryOptions.$apply = `${filterStr}/${groupByContextAggregationStr}/${groupByAggregationStr}/filter(AggregateValue gt 0d)`;

            return this.queryOData(queryOptions).then((data: CommonTypes.IODataQueryResponse) => {
                return this._getStackedChartData(confValues, data, CommonTypes.Metric.AvgDuration);
            });
        } else {
            throw new Error("Unable to fetch project details.");
        }
    }

    private _queryTestDurationStackedTrendData_TestResultDailyTable(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<ITrendChartData> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            const projectId = tfsPageData.project.id;
            const queryOptions = {
                entityType: this._testResultDailyEntityName,
                project: projectId
            } as ODataQueryOptions;

            const filterStr = `filter(${this._getTestResultsContextPipelineFilter(testReportContext)} and ${this._getDailyDateFilter(
                confValues.trendBy,
                confValues.period
            )} ${this._getFilterByFieldsString(confValues.configuredFilters)})`;
            const groupByContextAggregationStr = `groupby((${this._getGroupByTestPropertiesString(
                confValues.groupBy
            )}, ${this._getGroupByDailyPeriodColumnString(
                confValues.trendBy
            )}), aggregate(ResultDurationSeconds div ResultCount with sum as AggregateValue))`;

            // Filter out groups that have 0d(0.0) AggregateValue as that is not relevant to be shown in chart
            queryOptions.$apply = `${filterStr}/${groupByContextAggregationStr}/filter(AggregateValue gt 0d)`;

            return this.queryOData(queryOptions).then((data: CommonTypes.IODataQueryResponse) => {
                return this._getStackedChartData(confValues, data, CommonTypes.Metric.AvgDuration, this._parseReturnDailyDateIntoDisplayForm);
            });
        } else {
            throw new Error("Unable to fetch project details.");
        }
    }

    private _queryTestDurationStackedTrendData_TestResultTable(
        testReportContext: CommonTypes.ITestReportContext,
        confValues: CommonTypes.IReportConfiguration
    ): Promise<ITrendChartData> {
        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        if (tfsPageData && tfsPageData.project) {
            const projectId = tfsPageData.project.id;
            const queryOptions = {
                entityType: this._testResultEntityName,
                project: projectId
            } as ODataQueryOptions;

            this._getTestRunTitlePropertyString = () => {
                return "TestRun/Title";
            };

            const filterStr = `filter(${this._getTestResultsContextFilter(testReportContext)} and ${this._getDateFilter(
                confValues.trendBy,
                confValues.period
            )} ${this._getFilterByFieldsString(confValues.configuredFilters)})`;
            const groupByContextAggregationStr = `groupby((${this._getGroupByTestPropertiesString(
                confValues.groupBy
            )}, ${this._getGroupByPeriodColumnString(confValues.trendBy)}, ${this._getGroupByTestResultsContext(testReportContext)})
    , aggregate(DurationSeconds with sum as DurationSeconds))`;
            const groupByAggregationStr = `groupby((${this._getGroupByTestPropertiesString(confValues.groupBy)}, ${this._getGroupByPeriodColumnString(
                confValues.trendBy
            )}), aggregate(DurationSeconds with average as AggregateValue))`;

            // Filter out groups that have 0d(0.0) AggregateValue as that is not relevant to be shown in chart
            queryOptions.$apply = `${filterStr}/${groupByContextAggregationStr}/${groupByAggregationStr}/filter(AggregateValue gt 0d)`;

            return this.queryOData(queryOptions).then((data: CommonTypes.IODataQueryResponse) => {
                return this._getStackedChartData(confValues, data, CommonTypes.Metric.AvgDuration);
            });
        } else {
            throw new Error("Unable to fetch project details.");
        }
    }

    private _getStackedChartData(
        confValues: CommonTypes.IReportConfiguration,
        data: any,
        metricSelected: CommonTypes.Metric,
        dateParsingMethod?: (trendBy: CommonTypes.TrendBy, record: any) => string
    ): ITrendChartData {
        if (!data || !data.value) {
            throw new Error("Data is empty.");
        }

        const chartData: ITrendChartData = {
            primaryChartData: {},
            confValues: confValues,
            metricSelected: metricSelected,
            isCachedData: data.isCachedData
        };
        const stackByToTotalAggrValueMap: { [key: string]: number } = {};

        // Iterate over all data
        (data.value as any[]).forEach(d => {
            const date = dateParsingMethod ? dateParsingMethod(confValues.trendBy, d) : this._parseReturnDateIntoDisplayForm(confValues.trendBy, d);
            const stackByValue = this._parseTestProperties(confValues.groupBy, d).displayValue;

            if (!chartData.primaryChartData[stackByValue]) {
                chartData.primaryChartData[stackByValue] = [];
            }

            const aggrValue: number = d.AggregateValue as number;
            chartData.primaryChartData[stackByValue].push({
                date: date,
                aggrValue: aggrValue
            } as ITrendChartAggregateData);

            stackByToTotalAggrValueMap[stackByValue] = stackByToTotalAggrValueMap[stackByValue]
                ? stackByToTotalAggrValueMap[stackByValue] + aggrValue
                : aggrValue;
        });

        // Only select top artifacts by their aggregate values and club others into one group.
        return this._reduceAndCombineStackedChartSeries(chartData, stackByToTotalAggrValueMap, metricSelected);
    }

    private _reduceAndCombineStackedChartSeries(
        chartData: ITrendChartData,
        stackByToTotalAggrValueMap: { [key: string]: number },
        metricSelected: CommonTypes.Metric
    ): ITrendChartData {
        const filteredChartData: ITrendChartData = {
            primaryChartData: {},
            confValues: chartData.confValues,
            metricSelected: metricSelected,
            isCachedData: chartData.isCachedData
        };

        // Creating array from dictionary as sorting can be done on array and sort list by aggrvalue in descending order.
        const stackByTotalAggrValueList = Object.keys(stackByToTotalAggrValueMap).map(stackBy => {
            return { stackByValue: stackBy, aggrValue: stackByToTotalAggrValueMap[stackBy] };
        });
        stackByTotalAggrValueList.sort((aggr1, aggr2) => {
            return aggr2.aggrValue - aggr1.aggrValue;
        });

        // Populate top (n) stacked data.
        let count = 0;
        while (count < TrendChartOptions.maxStackedSeriesCount && count < stackByTotalAggrValueList.length) {
            const stackByValue: string = stackByTotalAggrValueList[count].stackByValue;

            // Sort a stacked series by date
            chartData.primaryChartData[stackByValue].sort((aggrData1, aggrData2) => Utils_String.localeComparer(aggrData1.date, aggrData2.date));

            filteredChartData.primaryChartData[stackByValue] = chartData.primaryChartData[stackByValue];

            count++;
        }

        if (count < stackByTotalAggrValueList.length) {
            // Populate remaining chart data as Others in new chart data.
            const remainingStackDateToAggrDataMap: { [key: string]: ITrendChartAggregateData } = {};
            let otherStackedDataCount = 0;

            Object.keys(chartData.primaryChartData).forEach((stackByValue: string) => {
                // Populate stack data if not populated in filtered list.
                if (!filteredChartData.primaryChartData[stackByValue]) {
                    otherStackedDataCount++;

                    chartData.primaryChartData[stackByValue].forEach((aggrData: ITrendChartAggregateData) => {
                        if (!remainingStackDateToAggrDataMap[aggrData.date]) {
                            remainingStackDateToAggrDataMap[aggrData.date] = { date: aggrData.date, aggrValue: 0 };
                        }

                        remainingStackDateToAggrDataMap[aggrData.date].aggrValue += aggrData.aggrValue;
                    });
                }
            });

            // Populate trend data for other stackByValues and sort trend data by date.
            const remainingStackedTrendChartAggrData = Object.keys(remainingStackDateToAggrDataMap).map(
                date => remainingStackDateToAggrDataMap[date]
            );
            remainingStackedTrendChartAggrData.sort((aggrData1, aggrData2) => Utils_String.localeComparer(aggrData1.date, aggrData2.date));
            filteredChartData.primaryChartData[
                Utils_String.format(Resources.OthersArtifactWithCountText, otherStackedDataCount)
            ] = remainingStackedTrendChartAggrData;
        }

        return filteredChartData;
    }

    private _queryTestOutcomeCountAndPassRateTrendData(
        queryOptions: ODataQueryOptions,
        confValues: CommonTypes.IReportConfiguration,
        dateParsingMethod?: (trendBy: CommonTypes.TrendBy, record: any) => string
    ): Promise<ITrendChartData> {
        const entityWiseOutcomeParseFunction: Function = this._getEntityWiseOutcomeCountParseFunction(queryOptions.entityType);
        const entityWiseTotalCountParseFunction: Function = this._getEntityWiseTotalCountParseFunction(queryOptions.entityType);

        return this.queryOData(queryOptions).then((data: CommonTypes.IODataQueryResponse) => {
            if (!data || !data.value) {
                throw new Error("Data is empty");
            }

            const chartData: ITrendChartData = {
                primaryChartData: { [this._emptyString]: [] },
                secondaryChartData: [],
                confValues: confValues,
                metricSelected: CommonTypes.Metric.ResultCountAndPassRate,
                isCachedData: data.isCachedData
            };

            // Iterate over all data
            (data.value as any[]).forEach(d => {
                const date = dateParsingMethod
                    ? dateParsingMethod(confValues.trendBy, d)
                    : this._parseReturnDateIntoDisplayForm(confValues.trendBy, d);

                let outcomeCount = 0;
                if (confValues.outcomes.length === 0) {
                    outcomeCount = entityWiseTotalCountParseFunction(d);
                } else {
                    confValues.outcomes.forEach(o => {
                        outcomeCount += entityWiseOutcomeParseFunction(o, d);
                    });
                }

                chartData.primaryChartData[this._emptyString].push({ date: date, aggrValue: outcomeCount });

                const totalCount: number = entityWiseTotalCountParseFunction(d);
                const notExecutedCount: number = entityWiseOutcomeParseFunction(CommonTypes.TestOutcome.NotExecuted, d);
                const passedCount: number = entityWiseOutcomeParseFunction(CommonTypes.TestOutcome.Passed, d);
                const notImpactedCount: number = entityWiseOutcomeParseFunction(CommonTypes.TestOutcome.NotImpacted, d);

                if (!chartData.secondaryChartData) {
                    chartData.secondaryChartData = [];
                }
                chartData.secondaryChartData.push({
                    date: date,
                    aggrValue: this._getPassPercentage(totalCount, passedCount, notExecutedCount, notImpactedCount)
                });
            });

            return chartData;
        });
    }

    private _queryTestOutcomeCountTrendData(
        queryOptions: ODataQueryOptions,
        confValues: CommonTypes.IReportConfiguration,
        dateParsingMethod?: (trendBy: CommonTypes.TrendBy, record: any) => string
    ): Promise<ITrendChartData> {
        const entityWiseOutcomeParseFunction: Function = this._getEntityWiseOutcomeCountParseFunction(queryOptions.entityType);
        const entityWiseTotalCountParseFunction: Function = this._getEntityWiseTotalCountParseFunction(queryOptions.entityType);

        return this.queryOData(queryOptions).then((data: CommonTypes.IODataQueryResponse) => {
            if (!data || !data.value) {
                throw new Error("Data is empty");
            }

            const chartData: ITrendChartData = {
                primaryChartData: { [this._emptyString]: [] },
                confValues: confValues,
                metricSelected: CommonTypes.Metric.ResultCount,
                isCachedData: data.isCachedData
            };

            // Iterate over all data
            (data.value as any[]).forEach(d => {
                const date = dateParsingMethod
                    ? dateParsingMethod(confValues.trendBy, d)
                    : this._parseReturnDateIntoDisplayForm(confValues.trendBy, d);

                let outcomeCount = 0;
                if (confValues.outcomes.length === 0) {
                    outcomeCount = entityWiseTotalCountParseFunction(d);
                } else {
                    confValues.outcomes.forEach(o => {
                        outcomeCount += entityWiseOutcomeParseFunction(o, d);
                    });
                }
                chartData.primaryChartData[this._emptyString].push({ date: date, aggrValue: outcomeCount });
            });

            return chartData;
        });
    }

    private _queryTestDurationTrendData(
        queryOptions: ODataQueryOptions,
        confValues: CommonTypes.IReportConfiguration,
        dateParsingMethod?: (trendBy: CommonTypes.TrendBy, record: any) => string
    ): Promise<ITrendChartData> {
        return this.queryOData(queryOptions).then((data: CommonTypes.IODataQueryResponse) => {
            if (!data || !data.value) {
                throw new Error("Data is empty");
            }

            const chartData: ITrendChartData = {
                primaryChartData: { [this._emptyString]: [] },
                confValues: confValues,
                metricSelected: CommonTypes.Metric.AvgDuration,
                isCachedData: data.isCachedData
            };

            // Iterate over all data
            (data.value as any[]).forEach(d => {
                const date = dateParsingMethod
                    ? dateParsingMethod(confValues.trendBy, d)
                    : this._parseReturnDateIntoDisplayForm(confValues.trendBy, d);
                chartData.primaryChartData[this._emptyString].push({ date: date, aggrValue: d.AvgDuration as number });
            });

            return chartData;
        });
    }
}
